import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { languageService } from '@/lib/languageService';
// Phase 77 — clear the per-tab workspace-selection flag on sign-out so the
// next authenticated session is forced through the Workspaces portal.
import { clearWorkspaceSelection } from '@/lib/auth/workspaceSession';
import type { Profile } from '@/types/database';

// ─── Context shape ────────────────────────────────────────────────────────────

interface AuthContextType {
  user:    User    | null;
  session: Session | null;
  /** Full profile row — available once the user is authenticated.
   *  Contains plan_tier, full_name, etc.  Null while loading or signed out. */
  profile: Profile | null;
  loading: boolean;
  /**
   * Phase 45 — true while a PASSWORD_RECOVERY session is active.
   * Set by onAuthStateChange when event === 'PASSWORD_RECOVERY'.
   * Consumed by PasswordRecoveryWatcher in App.tsx (navigates to /update-password)
   * and by UpdatePasswordPage (guards direct access).
   */
  isPasswordRecovery: boolean;
  signUp:  (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signIn:  (email: string, password: string, persist?: boolean) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  /** Force-refresh the profile row (e.g. after an admin upgrades the user). */
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,               setUser]               = useState<User    | null>(null);
  const [session,            setSession]            = useState<Session | null>(null);
  const [profile,            setProfile]            = useState<Profile | null>(null);
  const [loading,            setLoading]            = useState(true);
  // Phase 45: becomes true when Supabase fires the PASSWORD_RECOVERY event.
  // Resets to false on SIGNED_OUT or USER_UPDATED (password changed).
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  // ── Profile loader ─────────────────────────────────────────────────────────
  const loadProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('[AuthContext] loadProfile error:', error.message);
      return;
    }
    // If a profile doesn't exist yet (e.g. just signed up), bootstrap one.
    // The DB default for plan_tier is 'free' so no explicit value needed.
    if (!data) {
      const { data: created } = await supabase
        .from('profiles')
        .insert({ id: userId })
        .select()
        .single();
      setProfile(created as Profile | null);
    } else {
      setProfile(data as Profile);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user?.id) await loadProfile(user.id);
  }, [user?.id, loadProfile]);

  // ── Language load ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (user?.id) {
      languageService.loadUserLanguage(user.id);
    }
  }, [user?.id]);

  // ── Auth state subscription ────────────────────────────────────────────────
  useEffect(() => {
    // Restore existing session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // ── Phase 45: intercept PASSWORD_RECOVERY ─────────────────────────────
        // Supabase fires this event when the user follows a password-reset link.
        // We set the flag and bail out — normal sign-in logic must NOT run here
        // (no profile load, no dashboard redirect).
        // PasswordRecoveryWatcher in App.tsx watches this flag and navigates to
        // /update-password.
        if (event === 'PASSWORD_RECOVERY') {
          setIsPasswordRecovery(true);
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
          return;
        }

        // Clear the recovery flag on SIGNED_OUT (after successful update) or
        // USER_UPDATED (the password was changed via updateUser).
        if (event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
          setIsPasswordRecovery(false);
        }

        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          loadProfile(session.user.id);
        } else {
          setProfile(null);
        }
        setLoading(false);
      },
    );

    return () => subscription.unsubscribe();
  }, [loadProfile]);

  // ── Auth actions ───────────────────────────────────────────────────────────
  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) throw error;
      if (data.session) {
        setSession(data.session);
        setUser(data.user);
      }
      return { error: null };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const signIn = async (email: string, password: string, persist = true) => {
    try {
      if (!persist) {
        // Move token to sessionStorage so it is cleared when the tab closes
        localStorage.removeItem('axelio-auth-token');
        sessionStorage.setItem('axelio-session-only', '1');
      } else {
        sessionStorage.removeItem('axelio-session-only');
      }
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (!persist && data.session) {
        // Copy the token from localStorage (where the client wrote it) into
        // sessionStorage, then wipe the localStorage copy so it doesn't
        // survive a browser restart.
        const stored = localStorage.getItem('axelio-auth-token');
        if (stored) {
          sessionStorage.setItem('axelio-auth-token', stored);
          localStorage.removeItem('axelio-auth-token');
        }
      }
      setSession(data.session);
      setUser(data.user);
      return { error: null };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
      setProfile(null);
      // Phase 77 — drop the per-tab workspace-selection flag so the next
      // authenticated session always starts at /workspaces.
      clearWorkspaceSelection();
      sessionStorage.removeItem('axelio-session-only');
      sessionStorage.removeItem('axelio-auth-token');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user, session, profile, loading,
        isPasswordRecovery,
        signUp, signIn, signOut, refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
